import type { ReactNode } from 'react'
import { useCountUp, useInView, useSpotlight } from '../lib/hooks'
import { Reveal } from './Reveal'

/** Numbered section heading used across the site. */
export function SectionHead({
  index,
  kicker,
  title,
  lead,
  align = 'start',
}: {
  index?: string
  kicker: string
  title: ReactNode
  lead?: ReactNode
  align?: 'start' | 'center'
}) {
  return (
    <div className={align === 'center' ? 'section-head is-centered' : 'section-head'}>
      {index && (
        <Reveal as="span" variant="fade" className="section-index mono">
          §&nbsp;{index}
        </Reveal>
      )}
      <div>
        <Reveal as="p" variant="fade" className="kicker">
          {kicker}
        </Reveal>
        <Reveal as="h2" variant="mask" className="display-2" index={1}>
          {title}
        </Reveal>
        {lead && (
          <Reveal as="p" variant="up" className="lead" index={2}>
            {lead}
          </Reveal>
        )}
      </div>
    </div>
  )
}

/** Card with a pointer-tracked highlight and a hairline border. */
export function SpotlightCard({
  children,
  className,
  index = 0,
}: {
  children: ReactNode
  className?: string
  index?: number
}) {
  const spotlight = useSpotlight()
  return (
    <Reveal
      className={className ? `spot-card ${className}` : 'spot-card'}
      variant="up"
      index={index}
      {...spotlight}
    >
      {children}
    </Reveal>
  )
}

const formatValue = (value: number, decimals: number) =>
  value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })

/** Count-up figure. Starts when the strip scrolls into view. */
export function Stat({
  value,
  prefix,
  suffix,
  label,
  note,
  decimals = 0,
}: {
  value: number
  prefix?: string
  suffix?: string
  label: string
  note?: string
  decimals?: number
}) {
  const { ref, inView } = useInView<HTMLDivElement>()
  const current = useCountUp(value, inView)

  return (
    <div className="stat" ref={ref}>
      <p className="stat-value mono">
        {prefix && <em>{prefix}</em>}
        {formatValue(current, decimals)}
        {suffix && <em>{suffix}</em>}
      </p>
      <p className="stat-label">{label}</p>
      {note && <p className="stat-note mono">{note}</p>}
    </div>
  )
}

/** Infinite keyword band. Duplicated once so the loop is seamless. */
export function Marquee({ items, reverse = false }: { items: string[]; reverse?: boolean }) {
  const track = [...items, ...items]
  return (
    <div className="marquee" aria-hidden="true">
      <div className={reverse ? 'marquee-track is-reverse' : 'marquee-track'}>
        {track.map((item, index) => (
          <span key={`${item}-${index}`}>
            {item}
            <i>/</i>
          </span>
        ))}
      </div>
    </div>
  )
}
